import { apiFetch } from '@app/utils/utils';
import { AllowlistEntry, AuditLogEntry, TrialEntry, AddTrialInput } from '@app/Settings/types';

const ALLOWLIST = '/api/admin/signup-allowlist';
const TRIALS = '/api/admin/trial-allowlist';

export async function getAllowlist(): Promise<AllowlistEntry[]> {
  const res = await apiFetch(ALLOWLIST);
  if (!res.ok) throw new Error(`Failed to load allowlist (${res.status})`);
  return res.json();
}

export async function getAllowlistEnabled(): Promise<boolean> {
  const res = await apiFetch(`${ALLOWLIST}/enabled`);
  if (!res.ok) throw new Error(`Failed to load allowlist state (${res.status})`);
  const body = await res.json();
  return !!body.enabled;
}

export async function setAllowlistEnabled(enabled: boolean): Promise<boolean> {
  const res = await apiFetch(`${ALLOWLIST}/enabled`, {
    method: 'PUT',
    body: JSON.stringify({ enabled })
  });
  if (!res.ok) throw new Error(`Failed to update allowlist state (${res.status})`);
  const body = await res.json();
  return !!body.enabled;
}

export async function addAllowlistEntry(pattern: string): Promise<AllowlistEntry> {
  const res = await apiFetch(ALLOWLIST, { method: 'POST', body: JSON.stringify({ pattern }) });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to add entry (${res.status})`);
  }
  return res.json();
}

export async function removeAllowlistEntry(id: number): Promise<void> {
  const res = await apiFetch(`${ALLOWLIST}/${id}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Failed to remove entry (${res.status})`);
  }
}

export interface ProvisionedScreen {
  clientId: string;
  clientSecret: string;
  roles: string[];
}

/** Provision a new screen (ROLE_SCREEN service principal). The secret is returned once. */
export async function provisionScreen(displayName: string): Promise<ProvisionedScreen> {
  const res = await apiFetch('/api/admin/service-principals', {
    method: 'POST',
    body: JSON.stringify({ displayName, type: 'SCREEN', roles: ['ROLE_SCREEN'] })
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to provision screen (${res.status})`);
  }
  return res.json();
}

export async function getAuditLog(limit = 100): Promise<AuditLogEntry[]> {
  const res = await apiFetch(`/api/admin/audit-log?limit=${limit}`);
  if (!res.ok) throw new Error(`Failed to load audit log (${res.status})`);
  return res.json();
}

export async function getTrials(): Promise<TrialEntry[]> {
  const res = await apiFetch(TRIALS);
  if (!res.ok) throw new Error(`Failed to load trials (${res.status})`);
  return res.json();
}

export async function addTrial(input: AddTrialInput): Promise<TrialEntry> {
  const res = await apiFetch(TRIALS, { method: 'POST', body: JSON.stringify(input) });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to add trial (${res.status})`);
  }
  return res.json();
}

export async function removeTrial(id: number): Promise<void> {
  const res = await apiFetch(`${TRIALS}/${id}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Failed to remove trial (${res.status})`);
  }
}
